import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { adminChatAskPath } from "@gb/admin-contracts";
import {
  AskEventSchema,
  type AnswerConfidence,
  type AnswerCoverage,
  type AnswerStep,
  type AnswerTrail,
  type AnswerUnfinished,
  type AskStage,
  type ChatSource,
} from "@gb/contracts";
import { authorizedFetch } from "@/api/client";
import { cancelAsk, chatKeys } from "./chat-api";

/**
 * Asking a question through the admin ask route, and watching the answer
 * arrive.
 *
 * A rewrite of the dashboard's `use-chat-stream.ts` rather than an import —
 * the apps deliberately share contracts and not components (architecture/0022)
 * — against the admin path builder and this app's `authorizedFetch`. The
 * reasoning that travels: `fetch` + a stream reader because `EventSource` can
 * neither POST nor send an Authorization header, and every frame is parsed
 * against `AskEventSchema` before it is believed — this route is off the
 * ts-rest contract, so this is the only place the wire shape is checked.
 *
 * **The answering agent (Phase 5, 2026-09-26).** The stream starts at
 * `thinking`, and `step` frames arrive among the others: each step when it
 * starts and again when it finishes, upserted by `seq`. A turn's text is the
 * answer until the server sends a text note of that turn; then it is that
 * note, and the steps show it.
 *
 * **Stop goes through the database.** The answer runs on when its reader
 * leaves (§1.11 of the design), so closing the stream no longer stops it.
 * Stop, and a second question while one runs, call the admin twin of
 * `cancelAsk` first, then close the stream. On the old path, which slice X
 * removes, closing the stream still stops the answer.
 */

export type StreamState = {
  answer: string;
  sources: ChatSource[];
  stage: AskStage | null;
  reasoning: string;
  confidence: AnswerConfidence | null;
  confidenceNote: string | null;
  /** The steps so far, in order of `seq`. */
  steps: AnswerStep[];
  trail: AnswerTrail | null;
  coverage: AnswerCoverage | null;
  unfinished: AnswerUnfinished | null;
  pending: boolean;
  error: string | null;
};

const IDLE: StreamState = {
  answer: "",
  sources: [],
  stage: null,
  reasoning: "",
  confidence: null,
  confidenceNote: null,
  steps: [],
  trail: null,
  coverage: null,
  unfinished: null,
  pending: false,
  error: null,
};

/** The text of each turn, as it streamed. The old path sends no turn: `-1`. */
type TurnTexts = Map<number, string>;

/**
 * The answer as it stands: the text of the last turn that wrote text, unless
 * the server sent a text note of that turn, which makes it a note. On the old
 * path every delta is the answer.
 *
 * Only a `note` step in the `text` form counts (2026-09-27). The server sends
 * one for each text block it takes as a note, at the end of the turn. The
 * turn that writes the answer can also run code and think, and those steps
 * carry its turn number too: counting them blanked the answer at the end of
 * its own turn. The same rule as the dashboard's `streamedAnswer`.
 */
export function answerSoFar(texts: TurnTexts, steps: readonly AnswerStep[]): string {
  if (texts.size === 0) return "";
  const last = Math.max(...texts.keys());
  if (last < 0) return texts.get(last) ?? "";
  const noted = steps.some((step) => step.turn === last && step.kind === "note" && step.note?.form === "text");
  return noted ? "" : (texts.get(last) ?? "");
}

/** A step upserted by `seq`, in order. */
function upsert(steps: readonly AnswerStep[], step: AnswerStep): AnswerStep[] {
  return [...steps.filter((each) => each.seq !== step.seq), step].sort((a, b) => a.seq - b.seq);
}

export function useAdminChatStream(tenantId: string, projectId: string, threadId: string) {
  const [state, setState] = useState<StreamState>(IDLE);
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  /**
   * Stop the running answer through the database, then close its stream. The
   * request is sent before the stream closes, and not waited for: a Stop that
   * fails still closes the stream, and says so.
   */
  const stopRunning = useCallback(() => {
    const running = abortRef.current;
    if (!running) return;
    void cancelAsk(tenantId, projectId, threadId).catch((error: unknown) => {
      setState((prev) => ({
        ...prev,
        error: `Stop was not stored: ${error instanceof Error ? error.message : "the request failed"}.`,
      }));
    });
    running.abort();
  }, [tenantId, projectId, threadId]);

  const ask = useCallback(
    async (question: string): Promise<void> => {
      // A second question while one runs stops the first (`product/0005` §2.6).
      stopRunning();
      const controller = new AbortController();
      abortRef.current = controller;
      const texts: TurnTexts = new Map();

      setState({ ...IDLE, pending: true, stage: "thinking" });

      try {
        const response = await authorizedFetch(adminChatAskPath(tenantId, projectId, threadId), {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ question }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          // Pre-stream failures are ordinary HTTP — ownership, body shape and
          // the one-question-per-record rule are all checked before a byte.
          setState((prev) => ({
            ...prev,
            pending: false,
            error:
              response.status === 409
                ? "That question has already been answered."
                : response.status === 404
                  ? "That conversation no longer exists."
                  : "Couldn’t ask that question.",
          }));
          return;
        }

        for await (const event of readEvents(response.body)) {
          switch (event.type) {
            case "status":
              setState((prev) => ({ ...prev, stage: event.stage }));
              break;
            case "sources":
              setState((prev) => ({ ...prev, sources: event.sources }));
              break;
            case "reasoning":
              setState((prev) => ({ ...prev, reasoning: prev.reasoning + event.text }));
              break;
            case "delta": {
              const turn = event.turn ?? -1;
              texts.set(turn, (texts.get(turn) ?? "") + event.text);
              setState((prev) => ({ ...prev, answer: answerSoFar(texts, prev.steps) }));
              break;
            }
            case "step":
              setState((prev) => {
                const steps = upsert(prev.steps, event.step);
                return { ...prev, steps, answer: answerSoFar(texts, steps) };
              });
              break;
            case "error":
              setState((prev) => ({ ...prev, pending: false, error: event.message }));
              break;
            case "done":
              setState((prev) => ({
                ...prev,
                pending: false,
                stage: null,
                confidence: event.confidence,
                confidenceNote: event.confidenceNote,
                trail: event.trail ?? null,
                coverage: event.coverage ?? null,
                unfinished: event.unfinished ?? null,
              }));
              break;
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        setState((prev) => ({
          ...prev,
          pending: false,
          error: error instanceof Error ? error.message : "The answer was interrupted.",
        }));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          // Awaited, then cleared — the screen renders stored turns plus the
          // buffer, so clearing before the refetch lands blanks the answer for
          // a beat, and never clearing shows it twice. Same reconciliation the
          // dashboard's hook records. The steps are refetched with the thread.
          await queryClient.invalidateQueries({
            queryKey: chatKeys.thread(tenantId, projectId, threadId),
          });
          setState((prev) => (prev.error ? { ...IDLE, error: prev.error } : IDLE));
          void queryClient.invalidateQueries({
            queryKey: chatKeys.base(tenantId, projectId),
          });
        }
      }
    },
    [tenantId, projectId, threadId, queryClient, stopRunning],
  );

  const cancel = useCallback(() => {
    // Not cleared here — `ask`'s finally checks the ref before reconciling.
    stopRunning();
    setState((prev) => ({ ...prev, pending: false }));
  }, [stopRunning]);

  return { ...state, ask, cancel };
}

/**
 * SSE frames out of a byte stream. Same small parser as the dashboard's: the
 * server emits only `data:` lines separated by a blank line, and the buffer
 * handles a frame split across chunks mid-character. A `: ping` comment line
 * has no `data:` and is skipped.
 */
async function* readEvents(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (parsed) yield parsed;
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string) {
  const line = frame.split("\n").find((candidate) => candidate.startsWith("data:"));
  if (!line) return null;
  try {
    const parsed = AskEventSchema.safeParse(JSON.parse(line.slice(5).trim()));
    return parsed.success ? parsed.data : null;
  } catch {
    // Not JSON ⇒ not this API — a proxy error page. Drop it.
    return null;
  }
}
